// Pixel iklan per-toko (Meta Pixel, TikTok Pixel, GA4, GTM) — helper MURNI:
// nama key setelan, validator ID, guard halaman, dan pembangun payload event.
// Tanpa React/Supabase/window supaya seluruh aturannya teruji Vitest
// (pixel.test.ts); sisi yang menyentuh window ada di `pixelClient.ts` dan
// `PixelScripts.tsx`.
//
// ⚠️ Validator ID sekaligus PAGAR XSS: ID dari `site_settings` bisa ditulis
// dari jalur mana pun (form admin toko lain, SQL langsung, data lama), dan
// nilainya berakhir di dalam <script> yang dieksekusi browser pembeli. Regex
// whitelist di bawah adalah SATU-SATUNYA gerbang menuju interpolasi — ID yang
// tak lolos TIDAK dirender sama sekali (pixel mati, bukan "render apa adanya"),
// dan penyusun snippet tetap membungkus dengan JSON.stringify sebagai lapis
// kedua. Jangan pernah melonggarkan charset-nya jadi "string bebas".

import { isPlatformHost } from "@/lib/platformHost";

/**
 * Key `site_settings` untuk pixel — SATU sumber, diimpor `SITE_SETTING_KEYS`
 * (siteSettings.ts) dan pembaca server (pixelSettingsServer.ts), idiom yang
 * sama dengan `SHIPPING_SETTING_KEYS` di biteship.ts: dua salinan string key
 * yang menyimpang membuat layar admin menulis ke key yang tak pernah dibaca
 * storefront — fitur mati senyap tanpa error.
 *
 * Berpola `pixel_*` supaya platform berikutnya tinggal menambah key baru, dan
 * kelak token rahasia Conversions API bisa bernama `pixel_meta_capi_token`
 * (kelas key INTERNAL — kebalikan dari ID di bawah yang memang publik).
 * Key di bawah TIDAK boleh masuk denylist `site_setting_is_internal` (mig
 * 3550/3870): pengunjung anon storefront harus bisa membacanya; masuk denylist
 * = pixel mati senyap (jatuh ke default tanpa error).
 */
export const PIXEL_SETTING_KEYS = {
  metaPixelId: "pixel_meta_id",
  tiktokPixelId: "pixel_tiktok_id",
  ga4MeasurementId: "pixel_ga4_id",
  gtmContainerId: "pixel_gtm_id",
} as const;

/** Daftar key untuk query `.in()` / `key=in.(…)` pembaca server. */
export const PIXEL_SETTING_KEY_LIST: readonly string[] =
  Object.values(PIXEL_SETTING_KEYS);

/**
 * Key RAHASIA pixel server-side (Fase 2: Conversions API Meta / Events API
 * TikTok) — hidup di tabel `secure_settings` (admin-only, TANPA baca publik),
 * BUKAN `site_settings`. Dua kelas key ini berlawanan arah dan gampang
 * tertukar: ID pixel di atas WAJIB terbaca anon (tampil di HTML), token di
 * bawah TIDAK BOLEH pernah menyentuh browser pembeli — pembacanya hanya route
 * server `/api/pixel/conversions` via service role ber-scope toko.
 * `pixel_meta_test_code` bukan rahasia sungguhan, tapi ditaruh sekelas supaya
 * satu kartu admin = satu tabel = satu jalur baca.
 */
export const PIXEL_SECRET_SETTING_KEYS = {
  metaCapiToken: "pixel_meta_capi_token",
  metaTestEventCode: "pixel_meta_test_code",
  tiktokEventsToken: "pixel_tiktok_events_token",
  // Kode test TikTok TERPISAH dari Meta — tiap vendor menerbitkan kodenya
  // sendiri di tab Test Events masing-masing; satu kode bersama akan membuat
  // event uji vendor kedua tercatat sebagai event produksi sungguhan.
  tiktokTestEventCode: "pixel_tiktok_test_code",
} as const;

export const PIXEL_SECRET_SETTING_KEY_LIST: readonly string[] = Object.values(
  PIXEL_SECRET_SETTING_KEYS
);

/** ID pixel per-toko. "" = platform itu tidak dipakai (pixel mati). */
export type StorePixelIds = {
  metaPixelId: string;
  tiktokPixelId: string;
  ga4MeasurementId: string;
  gtmContainerId: string;
};

export const EMPTY_PIXEL_IDS: StorePixelIds = Object.freeze({
  metaPixelId: "",
  tiktokPixelId: "",
  ga4MeasurementId: "",
  gtmContainerId: "",
});

export type PixelKind = keyof StorePixelIds;

// Meta Pixel ID: numerik 15–16 digit (Meta Events Manager → Sumber Data).
const META_PIXEL_ID_RE = /^[0-9]{15,16}$/;
// TikTok Pixel ID: alfanumerik kapital ±20 char (TikTok Ads Manager → Aset →
// Event → Web Events). TikTok tidak mendokumentasikan panjang pastinya, jadi
// rentangnya sedikit longgar — charset-nya yang TIDAK boleh longgar.
const TIKTOK_PIXEL_ID_RE = /^[A-Z0-9]{15,24}$/;
// GA4 Measurement ID: G-XXXXXXXXXX (GA4 Admin → Aliran Data).
const GA4_MEASUREMENT_ID_RE = /^G-[A-Z0-9]{4,16}$/;
// GTM Container ID: GTM-XXXXXXX.
const GTM_CONTAINER_ID_RE = /^GTM-[A-Z0-9]{4,10}$/;

const PIXEL_ID_RES: Record<PixelKind, RegExp> = {
  metaPixelId: META_PIXEL_ID_RE,
  tiktokPixelId: TIKTOK_PIXEL_ID_RE,
  ga4MeasurementId: GA4_MEASUREMENT_ID_RE,
  gtmContainerId: GTM_CONTAINER_ID_RE,
};

/** Label + contoh format per platform untuk pesan error form admin. */
const PIXEL_FORMAT_HINTS: Record<PixelKind, string> = {
  metaPixelId: "Meta Pixel ID = 15–16 digit angka, contoh 1234567890123456",
  tiktokPixelId:
    "TikTok Pixel ID = huruf kapital & angka ±20 karakter, contoh C1234ABCD5EF6GH7IJ8K",
  ga4MeasurementId: "GA4 Measurement ID berawalan G-, contoh G-AB12CD34EF",
  gtmContainerId: "GTM Container ID berawalan GTM-, contoh GTM-AB12CD3",
};

/**
 * Normalisasi input ID dari form/DB: trim; selain Meta (numerik) dikapitalkan —
 * bentuk kanonik ketiga platform lain memang kapital, dan validator hanya
 * menerima kapital supaya satu ID punya SATU bentuk tersimpan.
 */
export function normalizePixelId(kind: PixelKind, raw: string): string {
  const trimmed = raw.trim();
  return kind === "metaPixelId" ? trimmed : trimmed.toUpperCase();
}

/** True bila `value` (SUDAH dinormalkan) sah untuk platform `kind`. */
export function isValidPixelId(kind: PixelKind, value: string): boolean {
  return PIXEL_ID_RES[kind].test(value);
}

/**
 * Deteksi admin menempel SNIPPET utuh (kode `<script>` dari Events Manager)
 * alih-alih ID-nya saja — kesalahan paling umum saat setup pixel.
 */
export function looksLikePixelSnippet(raw: string): boolean {
  return /<|>|\bfbq\s*\(|\bttq\b|\bgtag\s*\(|dataLayer|function\s*\(/i.test(raw);
}

/**
 * Validasi input form admin. `""` = valid (pixel dimatikan). Kembalikan pesan
 * error Indonesia bila tidak sah, `null` bila sah — pola `validateWhatsappInput`.
 */
export function validatePixelInput(kind: PixelKind, raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (looksLikePixelSnippet(trimmed)) {
    return "Tempel ID-nya saja, bukan seluruh kode script dari Events Manager.";
  }
  if (!isValidPixelId(kind, normalizePixelId(kind, trimmed))) {
    return `Format tidak dikenali. ${PIXEL_FORMAT_HINTS[kind]}.`;
  }
  return null;
}

/**
 * Saring nilai mentah (dari DB/props) → hanya ID yang LOLOS validator yang
 * dipertahankan; sisanya "" (pixel platform itu mati). Dipanggil pembaca server
 * SEBELUM ID masuk HTML, dan sekali lagi di komponen klien (lapis kedua).
 */
export function sanitizeStorePixelIds(
  raw: Partial<Record<PixelKind, string | null | undefined>>
): StorePixelIds {
  const out: StorePixelIds = { ...EMPTY_PIXEL_IDS };
  for (const kind of Object.keys(PIXEL_ID_RES) as PixelKind[]) {
    const normalized = normalizePixelId(kind, raw[kind] ?? "");
    if (normalized && isValidPixelId(kind, normalized)) out[kind] = normalized;
  }
  return out;
}

/** Ada minimal satu pixel terpasang? (gerbang render `<PixelScripts>`) */
export function hasAnyPixel(ids: StorePixelIds): boolean {
  return Boolean(
    ids.metaPixelId || ids.tiktokPixelId || ids.ga4MeasurementId || ids.gtmContainerId
  );
}

/**
 * Halaman ini boleh memuat/menembakkan pixel?
 *
 * Urutan pagar meniru `VisitorTracker.tsx` PERSIS — HOST platform lebih dulu:
 * di apex kelolify.com, `/` di-REWRITE ke `/platform` oleh middleware sehingga
 * `window.location.pathname` TETAP "/" dan cek path tak pernah menyala di sana.
 * Lalu path: /admin & /platform (panel internal — event admin mengotori data
 * iklan toko), plus /toko (katalog reseller white-label — keputusan pemilik
 * 2026-08: traffic reseller TIDAK di-track supaya data ads toko bersih).
 *
 * Dipanggil ulang di SETIAP penembakan event, bukan hanya saat mount: navigasi
 * client-side (next/link) tidak me-remount root layout, jadi base pixel yang
 * sudah hidup ikut terbawa saat pembeli berpindah ke /admin/login — event-nya
 * yang harus berhenti.
 */
export function isTrackablePage(hostname: string, pathname: string): boolean {
  if (isPlatformHost(hostname)) return false;
  if (pathname.startsWith("/admin") || pathname.startsWith("/platform")) {
    return false;
  }
  if (pathname === "/toko" || pathname.startsWith("/toko/")) return false;
  return true;
}

// ─── Payload event (netral-vendor) ───────────────────────────────────────────
// Satu bentuk kanonik yang dibangun titik-titik alur belanja, lalu dipetakan
// per-vendor oleh `toMetaParams`/`toTiktokParams`/`toGa4Params` — supaya aturan
// bentuknya (value opsional, IDR utuh, agregasi qty) hidup di SATU tempat yang
// teruji, bukan tersebar di empat pemetaan.

/** Baris isi event — `id` = product.id (uuid), keputusan pemilik 2026-08.
 *  Di katalog Meta id ini = `item_group_id` (feed Katalog Instagram per
 *  varian, dan kolom yang sama di CSV `/api/pixel/catalog`) — karena itu Meta
 *  menerimanya ber-`content_type` `META_CONTENT_TYPE`. */
export type PixelContent = {
  id: string;
  name?: string;
  quantity: number;
  /** Harga satuan rupiah UTUH (tanpa sen, tanpa /100). undefined = tak dikirim. */
  price?: number;
};

export type PixelEventPayload = {
  /**
   * Total nilai event, rupiah utuh. `undefined` = JANGAN kirim field-nya sama
   * sekali (harga menyusul / subtotal belum diketahui) — bukan kirim 0, karena
   * 0 terbaca "konversi tak bernilai" di pelaporan ads.
   */
  value?: number;
  currency: "IDR";
  contents: PixelContent[];
  /** Dedup Purchase = order_code — deterministik supaya kelak bisa
   *  direkonstruksi server-side (Conversions API) untuk dedup pixel↔CAPI. */
  eventId?: string;
};

/** Bentuk keranjang minimum yang dibutuhkan pembangun payload (struktural —
 *  `CartState` dari cartMulti.ts langsung cocok tanpa impor silang). */
export type PixelCartLine = { qty: number; unitPrice?: number };
export type PixelCart = {
  productId: string;
  productName: string;
  lines: PixelCartLine[];
};

export function buildViewContentPayload(input: {
  productId: string;
  productName: string;
  /** Harga PUBLIK (grup null — harga khusus reseller tak bocor ke payload ads). */
  price?: number;
}): PixelEventPayload {
  return {
    value: input.price,
    currency: "IDR",
    contents: [
      { id: input.productId, name: input.productName, quantity: 1, price: input.price },
    ],
  };
}

export function buildAddToCartPayload(input: {
  productId: string;
  productName: string;
  qty: number;
  /**
   * Harga satuan TAMU (`guestSellablePrice`, pricing.ts) — BUKAN harga yang
   * dilihat pembeli yang sedang login: harga level reseller/agen rahasia toko
   * (B7, PR-IG-4). undefined = "harga menyusul".
   */
  unitPrice?: number;
}): PixelEventPayload {
  return {
    value: input.unitPrice !== undefined ? input.unitPrice * input.qty : undefined,
    currency: "IDR",
    contents: [
      {
        id: input.productId,
        name: input.productName,
        quantity: input.qty,
        price: input.unitPrice,
      },
    ],
  };
}

/**
 * Agregasi baris keranjang → satu `PixelContent` per (PRODUK × HARGA SATUAN).
 *
 * BUKAN satu entri per produk (bentuk lama): `item_price` cuma sah bila seragam
 * di dalam entri, jadi satu entri per produk memaksa harga DIBUANG begitu
 * varian-variannya berbeda harga — hal yang rutin di grosir fashion. Audit
 * 2026-09-21: 280 dari 2.430 order Galea 90 hari kehilangan harga persis karena
 * itu (26,4% bersama baris "harga menyusul"), dan Meta melaporkannya sebagai
 * "data harga hilang" pada peristiwa Purchase.
 *
 * Mengelompokkan per harga membuat `item_price` SELALU terisi untuk baris yang
 * punya harga, tanpa mengarang rata-rata, dan menjaga invarian
 * `value` = Σ(item_price × quantity) tetap persis. Baris "harga menyusul"
 * berkumpul di SATU entri tanpa price — qty-nya tetap terhitung.
 *
 * Jangan hitung dari jumlah baris: kirim-sebagian memecah baris order_items,
 * dan satu produk bisa punya banyak baris varian.
 */
function cartContents(carts: PixelCart[]): PixelContent[] {
  const out: PixelContent[] = [];
  for (const cart of carts) {
    // `null` = kelompok "harga menyusul". Map menjaga urutan kemunculan baris,
    // jadi entri pertama produk tetap baris pertama keranjang.
    const qtyByPrice = new Map<number | null, number>();
    for (const line of cart.lines) {
      const key = line.unitPrice ?? null;
      qtyByPrice.set(key, (qtyByPrice.get(key) ?? 0) + line.qty);
    }
    for (const [price, quantity] of qtyByPrice) {
      out.push({
        id: cart.productId,
        name: cart.productName,
        quantity,
        price: price ?? undefined,
      });
    }
  }
  return out;
}

/** InitiateCheckout: seluruh keranjang yang akan dipesan + subtotal layar
 *  (`null` = ada baris "harga menyusul" → value tak dikirim). */
export function buildCheckoutPayload(
  carts: PixelCart[],
  subtotal: number | null
): PixelEventPayload {
  return {
    value: subtotal ?? undefined,
    currency: "IDR",
    contents: cartContents(carts),
  };
}

/**
 * Purchase: SATU payload per order (checkout multi-produk membuat satu order
 * terpisah per produk — masing-masing dengan order_code sendiri).
 *
 * `eventId` = order_code → Meta/TikTok men-dedup event ber-ID sama. Keputusan
 * sadar pemilik (2026-08): checkout preorder bisa MERGE ke order lama
 * (data.ts) dan memulangkan order_code LAMA — Purchase kedua itu memang
 * ter-dedup, data ads bersih dari dobel lebih penting daripada nilai tambahan.
 * `orderCode` null (Supabase tak tersedia / order lokal) → tanpa eventId.
 */
export function buildPurchasePayload(input: {
  cart: PixelCart;
  subtotal: number | null;
  orderCode: string | null;
}): PixelEventPayload {
  return {
    value: input.subtotal ?? undefined,
    currency: "IDR",
    contents: cartContents([input.cart]),
    eventId: input.orderCode ?? undefined,
  };
}

// ─── Pemetaan per-vendor ─────────────────────────────────────────────────────

export type PixelEventName =
  | "ViewContent"
  | "AddToCart"
  | "InitiateCheckout"
  | "Purchase";

/** Nama event GA4 (dipakai gtag maupun push dataLayer untuk GTM). */
export const GA4_EVENT_NAMES: Record<PixelEventName, string> = {
  ViewContent: "view_item",
  AddToCart: "add_to_cart",
  InitiateCheckout: "begin_checkout",
  Purchase: "purchase",
};

/**
 * Nama event TikTok. Purchase → "PlaceAnOrder": checkout di sini MEMBUAT
 * pesanan, pembayarannya menyusul via nota/DP — "CompletePayment" akan
 * berbohong. Kampanye TikTok toko dioptimasi ke PlaceAnOrder (tertulis di
 * teks bantuan halaman admin); CompletePayment kandidat Conversions API saat
 * nota lunas (Fase 2).
 */
export const TIKTOK_EVENT_NAMES: Record<PixelEventName, string> = {
  ViewContent: "ViewContent",
  AddToCart: "AddToCart",
  InitiateCheckout: "InitiateCheckout",
  Purchase: "PlaceAnOrder",
};

/**
 * `content_type` SEMUA event Meta ber-isi produk — pixel browser DAN
 * Conversions API (`pixelServerEvents.ts`) — SATU konstanta (PR-IG-4,
 * keputusan pemilik 2026-09-29).
 *
 * `content_ids` kita = products.id, sedangkan item katalog Meta = VARIAN
 * (feed Katalog Instagram: `id` = varian, `item_group_id` = produk). Dengan
 * "product", Meta mencari id produk di antara id VARIAN dan tak menemukan
 * apa pun — event tak pernah tersambung ke katalog. "product_group" membuat
 * Meta mencocokkannya ke `item_group_id`. CSV lama `/api/pixel/catalog`
 * (satu baris per produk) ikut memancarkan `item_group_id` = id supaya
 * katalog yang dibangun darinya tetap cocok. Jangan kembalikan ke "product"
 * tanpa mengganti `content_ids` menjadi id varian di semua event.
 */
export const META_CONTENT_TYPE = "product_group";

/**
 * Parameter `fbq('track', …)` Meta.
 *
 * ⚠️ `content_name` (nama produk) SENGAJA TIDAK dikirim sejak 2026-09-21.
 * Itu satu-satunya teks produk yang pernah menyentuh Meta, dan pada katalog
 * fashion muslim ia berbunyi sebagai KATEGORI SENSITIF — "terkait individu
 * dengan keyakinan agama tertentu" menurut Ketentuan Fitur Meta Business, yang
 * memblokir data situs dari Wilayah Eropa. Di Galea 372 dari 1.140 produk aktif
 * memuat hijab/khimar/gamis/syar'i/mukena. `content_ids` (product.id — uuid
 * tanpa makna) tetap membawa identitas produk untuk pelaporan & Dynamic Ads.
 * TikTok/GA4 TIDAK ikut dicabut: larangan ini khusus Meta, dan nama produk di
 * sana masih berguna. Jangan kembalikan tanpa memeriksa ketentuan itu dulu.
 */
export function toMetaParams(p: PixelEventPayload): Record<string, unknown> {
  const params: Record<string, unknown> = {
    content_type: META_CONTENT_TYPE,
    // Dedup: satu produk kini bisa punya BEBERAPA entri `contents` (satu per
    // harga satuan), sedangkan `content_ids` adalah daftar PRODUK — id kembar
    // di sana terbaca Meta sebagai dua produk berbeda.
    content_ids: [...new Set(p.contents.map((c) => c.id))],
    contents: p.contents.map((c) => ({
      id: c.id,
      quantity: c.quantity,
      ...(c.price !== undefined ? { item_price: c.price } : {}),
    })),
    currency: p.currency,
  };
  if (p.value !== undefined) params.value = p.value;
  return params;
}

/** Parameter `ttq.track(…)` TikTok. */
export function toTiktokParams(p: PixelEventPayload): Record<string, unknown> {
  const params: Record<string, unknown> = {
    content_type: "product",
    contents: p.contents.map((c) => ({
      content_id: c.id,
      quantity: c.quantity,
      ...(c.name ? { content_name: c.name } : {}),
      ...(c.price !== undefined ? { price: c.price } : {}),
    })),
    currency: p.currency,
  };
  if (p.value !== undefined) params.value = p.value;
  return params;
}

/** Parameter event GA4 (`gtag('event', …)` / `dataLayer.push({ecommerce})`). */
export function toGa4Params(p: PixelEventPayload): Record<string, unknown> {
  const params: Record<string, unknown> = {
    currency: p.currency,
    items: p.contents.map((c) => ({
      item_id: c.id,
      quantity: c.quantity,
      ...(c.name ? { item_name: c.name } : {}),
      ...(c.price !== undefined ? { price: c.price } : {}),
    })),
  };
  if (p.value !== undefined) params.value = p.value;
  if (p.eventId !== undefined) params.transaction_id = p.eventId;
  return params;
}

// ─── ID Klik iklan (fbc) ─────────────────────────────────────────────────────

/**
 * Cookie first-party CADANGAN untuk `fbc`, ditulis kode aplikasi sendiri.
 *
 * Nama sengaja BUKAN `_fbc`: itu milik fbevents.js, dan dua penulis atas satu
 * nama cookie berakhir saling menimpa dengan format yang berbeda versi.
 * Prefiks `mh_` mengikuti identitas teknis yang tidak ikut rebranding Kelolify
 * (sekelas `mh-pixel-base` di PixelScripts).
 */
export const FBC_FALLBACK_COOKIE = "mh_fbc";

/** Jendela atribusi klik terpanjang Meta (90 hari). Checkout preorder memang
 *  bisa berjarak berminggu-minggu dari klik iklan yang membayarnya. */
export const FBC_FALLBACK_MAX_AGE_SEC = 90 * 24 * 60 * 60;

// `fbclid` yang sah dari Meta berupa token URL-safe. Whitelist ini gerbang
// terhadap nilai yang diarang siapa pun lewat query string — isinya berakhir di
// cookie dan di body request ke Graph API.
const FBCLID_RE = /^[A-Za-z0-9._-]{1,500}$/;

/**
 * Susun `fbc` (ID Klik) dari `fbclid` mentah di URL pendaratan iklan.
 *
 * Format WAJIB Meta: `fb.<subdomainIndex>.<creationTimeMs>.<fbclid>`; index 1
 * berarti cookie ditulis di domain toko itu sendiri. Bentuk yang menyimpang
 * ditolak diam-diam oleh Graph API, jadi ia dirakit di satu tempat yang teruji.
 *
 * Dipakai HANYA sebagai cadangan saat cookie `_fbc` tak pernah lahir — mis.
 * pembeli memakai ad-blocker yang memblokir fbevents.js, justru kasus yang
 * membuat Conversions API ada. Cookie asli SELALU menang di sisi server: Meta
 * mencocokkan atas nilai yang sama persis dengan yang dilihat browser.
 *
 * `null` = tak ada / tak sah → JANGAN kirim parameternya sama sekali.
 */
export function buildFbcFromFbclid(
  fbclid: string | null | undefined,
  creationTimeMs: number
): string | null {
  const raw = (fbclid ?? "").trim();
  if (!raw || !FBCLID_RE.test(raw)) return null;
  if (!Number.isFinite(creationTimeMs) || creationTimeMs <= 0) return null;
  return `fb.1.${Math.floor(creationTimeMs)}.${raw}`;
}
